// Phase 3.4 — Sales Return screen, migrated to shadcn/Tailwind.
//
// UI only: the MUI/antd Table, the ApparelSubmenu toolbar, the antd Drawer form
// (AutoComplete/Select/MUI RadioGroup) and the antd delete Modal are replaced by
// the shared DataTable (SalesReturnTable), a PageHeader + built-in
// search/pagination, a Sheet (SalesReturnFormSheet) and ConfirmDialog. Every
// dispatch / useSelector / payload is unchanged — all handlers below are the
// legacy ones. The antd Form is dropped: inputs are controlled by
// `salesReturnFormData` (which the antd fields mirrored), so the `form.setFieldsValue`
// calls are removed; the dispatched payload is byte-for-byte the same.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import ApparelMenu from "./SubComponents/apparelMenu";
import AppConstant from "../Globals/AppConstants";
import { useDispatch, useSelector } from "react-redux"
import { deepCopyFunction } from "../Utils/helpers";
import { categoryAndSubCategoryListAction, clearSalesReturnBasedOnProductIdAction, createOrUpdateSalesReturnAction, getBillingListAction, getSalesReturnAction, getSalesReturnBasedOnProductIdAction, getSalesReturnBillDetailsAction } from "../Store/actions/apparelAction";
import { ConfirmDialog } from "../shared/confirm-dialog";
import { Button } from "ui/button";
import SalesReturnTable from "./salesReturn/SalesReturnTable";
import SalesReturnFormSheet from "./salesReturn/SalesReturnFormSheet";
import SalesReturnBillFormSheet from "./salesReturn/SalesReturnBillFormSheet";

// createOrUpdateSalesReturn's success answers (AppConstants on the API).
const SALES_RETURN_SUCCESS_MESSAGES = ["Created Successfully", "Updated Successfully", "Deleted Successfully"];

const SalesReturn = () => {
    const searchtemp: any = {
        searchText: "",
        returnTypeFilter: "",
        // "" = All, "credit"/"no_credit" — see handleCreditFilterChange.
        creditFilter: "",
        offset: 0,
        limit: 10,
        page: 1,
    };

    const salesReturnObj = {
        salesReturnId: null,
        productId: null,
        categoryId: null,
        subCategoryId: null,
        mrp: null,
        invoiceId: null,
        invoice: null,
        qty: 1,
        returnedAmount: null,
        isMultipleRecords: 1,
        qtyLeft: 1,
        status: null,
        statusRefId: 4,
    }

    const location = useLocation();
    const navigate = useNavigate();
    // Arriving from Audit Log: re-run this screen's own search with the
    // record's invoice id, then open its edit sheet once the matching row
    // (by salesReturnId, the uniqueKey alias) shows up in the list.
    const [pendingAuditRecordId, setPendingAuditRecordId] = useState<string | undefined>(location.state?.auditRecordId);
    const [filterData, setFilterData] = useState<any>(() => {
        const auditCode = location.state?.auditSearchCode;
        return auditCode ? { ...deepCopyFunction(searchtemp), searchText: auditCode } : deepCopyFunction(searchtemp);
    });
    const [open, setOpen] = useState<any>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<any>(false);
    const [page] = useState(1);
    const [salesReturnFormData, setSalesReturnFormData] = useState<any>(deepCopyFunction(salesReturnObj));
    const dispatch = useDispatch();
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { categoryAndSubCategoryData, getSalesReturnData,
        createOrUpdateSalesReturnOnLoad, createOrUpdateSalesReturn,
        getSalesReturnBasedOnProductId, getSalesReturnBasedOnProductIdOnLoad,
        billingList, billingListOnload, getSalesReturnBillDetailsData, getSalesReturnBillDetailsOnLoad } = apparelReducerState;
    const [subCategoryList, setSubCategoryList] = useState<any>([]);
    const [deleteRecord, setDeleteRecord] = useState<any>(0);
    const [salesReturnLoad, setSalesReturnLoad] = useState(false);
    const [editRecord, setEditRecord] = useState(0);
    const [invoiceId, setInvoiceId] = useState<any>([]);
    const [quantity, setQuantity] = useState<any>(null);
    const [salesReturnSearchSuccess, setSalesReturnSearchSuccess] = useState<any>(false);
    const [productIdSearch, setProductIdSearch] = useState<any>(null)
    const categoryAndSubCategoryList = categoryAndSubCategoryData?.getCategoryList;

    // Bill-first "+Add" create flow — kept fully separate from the
    // product-first state above, which still drives edit/delete of an
    // existing sales-return record via SalesReturnFormSheet.
    const [addOpen, setAddOpen] = useState(false);
    const [billSearchText, setBillSearchText] = useState("");
    const [selectedBill, setSelectedBill] = useState<any>(null);
    const [billReturnSubmitting, setBillReturnSubmitting] = useState(false);
    // Invoice Id column click — looks up the full billing row (this screen's
    // list rows don't carry customer/payment/total fields CreateBill needs)
    // by invoice text, same search billingList already powers, then navigates
    // once it comes back.
    const [pendingInvoiceNavigate, setPendingInvoiceNavigate] = useState<string | undefined>(undefined);

    let getSalesReturnSearchData = getSalesReturnBasedOnProductId?.getAddSalesReturnList;
    let salesReturnData = getSalesReturnData?.getSalesReturnList?.salesRetrunDetails;
    let salesReturnPagination = getSalesReturnData?.getSalesReturnList?.page;
    let billSearchResults = billingList?.getBillingList?.billingDetails;
    let billReturnDetails = getSalesReturnBillDetailsData?.getSalesReturnBillDetails;

    const getSalesReturnListApi = () => {
        let payload = {
            searchText: filterData?.searchText,
            statusRefId: filterData?.returnTypeFilter ? Number(filterData.returnTypeFilter) : null,
            hasCreditPayment: filterData?.creditFilter === "credit" ? true : filterData?.creditFilter === "no_credit" ? false : null,
            limit: 10,
            offset: filterData?.offset
        }
        dispatch(getSalesReturnAction(payload))
    }

    useEffect(() => {
        dispatch(categoryAndSubCategoryListAction(''));
    }, []);

    useEffect(() => {
        getSalesReturnListApi()
    }, [filterData?.searchText, filterData?.returnTypeFilter, filterData?.creditFilter, filterData?.offset])

    const getsearchTextdataList = (searchText: any) => {
        setFilterData({ ...filterData, searchText: searchText, offset: 0 });
    }

    const handleReturnTypeFilterChange = (value: any) => {
        setFilterData({ ...filterData, returnTypeFilter: value, offset: 0, page: 1 });
    }

    const handleCreditFilterChange = (value: any) => {
        setFilterData({ ...filterData, creditFilter: value, offset: 0, page: 1 });
    }

    const handlePagination = (page: any, offset: any) => {
        setFilterData({ ...filterData, page: page, offset: offset })
    }

    // DataTable gives a 1-based page; translate to the legacy offset math.
    const onPageChange = (nextPage: number) => {
        let offset = ((nextPage * 10) - 10 === 0) ? 0 : (nextPage * 10) - 10;
        handlePagination(page, offset);
    }

    const prepopulate = (data: any) => {
        setSalesReturnFormData({
            ...salesReturnFormData,
            salesReturnId: data?.salesReturnId,
            productId: data?.productId,
            categoryId: data?.categoryId,
            mrp: data?.mrp,
            subCategoryId: data?.subCategoryId,
            invoiceId: data?.invoiceId,
            invoice: data?.invoice,
            qty: data?.quantity,
            qtyLeft: data?.quantityLeft,
            returnedAmount: data?.returnedAmount,
            isMultipleRecords: data?.isMultipleRecords,
            status: data?.status,
            statusRefId: data?.statusRefId ? data?.statusRefId : salesReturnFormData?.statusRefId,
        });
    }

    const getSalesReturnProductIdApi = (id: any) => {
        let payload = {
            productId: id,
            isEdit: editRecord
        }
        dispatch(getSalesReturnBasedOnProductIdAction(payload));
        setSalesReturnSearchSuccess(true);
    }

    useEffect(() => {
        if (salesReturnSearchSuccess && !getSalesReturnBasedOnProductIdOnLoad) {
            setSalesReturnSearchSuccess(false)
            if (editRecord == 1) {
                handleProductIdSelect(salesReturnFormData?.productId);
                if (salesReturnFormData?.invoiceId)
                    handleInvoiceId(salesReturnFormData?.invoiceId)
                setOpen(true);
            }
        }
    }, [salesReturnSearchSuccess, getSalesReturnBasedOnProductIdOnLoad, editRecord])

    useEffect(() => {
        if (salesReturnFormData?.categoryId != null) {
            const filterCategoryValue = categoryAndSubCategoryList?.filter((x: any) => x.categoryId == salesReturnFormData?.categoryId);
            (filterCategoryValue || []).map((item: any) => {
                (item?.subCategories || []).map((subCategoryData: any) => {
                    let obj = {
                        subCategoryId: subCategoryData?.subCategoryId,
                        subCategoryName: subCategoryData?.subCategoryName
                    }
                    setSubCategoryList((prev: any) => [...prev, obj]);
                })
            })
        }
    }, [salesReturnFormData?.categoryId])

    useEffect(() => {
        if (productIdSearch) {
            const getData = setTimeout(() => {
                getSalesReturnProductIdApi(productIdSearch)
            }, 800);
            return () => clearTimeout(getData)
        }
    }, [productIdSearch])

    const handleProductIdSearch = (value: any) => {
        setProductIdSearch(value)
    };

    const createOrUpdateSalesReturnApi = () => {
        let statusRefIdSwitch;
        switch (salesReturnFormData?.statusRefId) {
            case 2: statusRefIdSwitch = AppConstant.inStock;
                break;
            case 5: statusRefIdSwitch = AppConstant.damaged;
                break;
            case 9: statusRefIdSwitch = AppConstant.scrap;
                break;
            case 11: statusRefIdSwitch = AppConstant.CANCELLED;
                break;
            default: statusRefIdSwitch = AppConstant.returned;
                break;
        }

        let payload = {
            salesReturnId: salesReturnFormData?.salesReturnId,
            productItemId: salesReturnFormData?.productId,
            invoiceId: salesReturnFormData?.invoiceId,
            categoryId: salesReturnFormData?.categoryId,
            subCategoryId: salesReturnFormData?.subCategoryId,
            qty: salesReturnFormData?.qty,
            returnedAmount: salesReturnFormData?.returnedAmount,
            mrp: Number(salesReturnFormData?.mrp),
            isDeleted: deleteRecord,
            isMultipleRecords: salesReturnFormData?.isMultipleRecords,
            status: statusRefIdSwitch,
            statusRefId: salesReturnFormData?.statusRefId ? salesReturnFormData?.statusRefId : 4
        }
        dispatch(createOrUpdateSalesReturnAction(payload))
        setSalesReturnLoad(true);
    }

    useEffect(() => {
        if (salesReturnLoad && !createOrUpdateSalesReturnOnLoad) {
            const wasEdit = editRecord == 1;
            // The API answers with a message string. A non-success one (e.g. a
            // combo return whose break-up can't be undone because an item no
            // longer has the stock) means nothing was saved — say so instead.
            const message = createOrUpdateSalesReturn?.createOrUpdateSalesReturn;
            if (typeof message === "string" && !SALES_RETURN_SUCCESS_MESSAGES.includes(message)) {
                toast.error(message);
                setSalesReturnLoad(false);
                setOpenDeleteModal(false);
                return;
            }
            AddProductModalClose();
            toast.success(wasEdit ? "Sales Return updated successfully" : "Sales Return added successfully");
            setSalesReturnLoad(false);
            setOpenDeleteModal(false);
            getSalesReturnListApi();
        }
    }, [salesReturnLoad, createOrUpdateSalesReturnOnLoad])

    useEffect(() => {
        if (editRecord == 1) {
            getSalesReturnProductIdApi(salesReturnFormData?.productId);
        }
    }, [editRecord])

    const handleEditOrDelete = (data: any, id: any) => {
        prepopulate(data);
        if (id == '1') {
            setEditRecord(1);
        } else {
            setDeleteRecord(1)
            setOpenDeleteModal(true);
        }
    }

    useEffect(() => {
        if (!pendingAuditRecordId || !salesReturnData?.length) return;
        const match = salesReturnData.find((row: any) => row?.salesReturnId === pendingAuditRecordId);
        if (match) {
            handleEditOrDelete(match, '1');
            setPendingAuditRecordId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAuditRecordId, salesReturnData])

    const handleDeleteModalClose = () => {
        setOpenDeleteModal(false)
        AddProductModalClose();
    }

    const AddProductModalClose = () => {
        setOpen(false);
        setProductIdSearch(null);
        setSubCategoryList([]);
        setInvoiceId([]);
        setQuantity(null);
        setDeleteRecord(0);
        setEditRecord(0);
        dispatch(clearSalesReturnBasedOnProductIdAction(""));
        setSalesReturnFormData(salesReturnObj);
    }

    const handleQuantity = (value: any) => {
        setSalesReturnFormData({ ...salesReturnFormData, qty: Number(value) })
    }

    const handleProductIdChange = (productId: any) => {
        setSalesReturnFormData({ ...salesReturnFormData, productId: productId });
        setProductIdSearch(productId);
    }

    const handleProductIdSelect = (productId: any) => {
        setInvoiceId([]);
        setProductIdSearch(productId);
        let productData = (getSalesReturnSearchData || []).find((x: any) => x?.productId == productId);
        setSalesReturnFormData({
            ...salesReturnFormData,
            productId: productId,
            categoryId: productData?.categoryId,
            subCategoryId: productData?.subCategoryId,
            isMultipleRecords: productData?.isMultipleRecords,
        });

        const uniqueInvoiceIds = new Set();
        (productData?.data || []).forEach((item: any) => {
            if (!uniqueInvoiceIds.has(item?.invoiceId)) {
                uniqueInvoiceIds.add(item?.invoiceId);
                let obj = {
                    invoiceId: item?.invoiceId,
                    invoice: item?.invoice
                };
                setInvoiceId((prev: any) => [...prev, obj]);
            }
        });
    };

    // Only ever runs now as part of edit-population (the Invoice Id field
    // itself is disabled/read-only) — its job is to backfill the bill-item's
    // mrp/qtyLeft for validation context. It must NOT touch qty/status/
    // statusRefId: those belong to the sales-return record itself and are
    // already set correctly by prepopulate() from the record being edited,
    // not from how much was originally billed.
    const handleInvoiceId = (value: any) => {
        const getDataBasedOnInvoiceId = (getSalesReturnSearchData?.[0]?.data || []).find((x: any) => x.invoiceId == value);
        setQuantity(getDataBasedOnInvoiceId?.quantityLeft == 0 ? getDataBasedOnInvoiceId?.quantity
            : getDataBasedOnInvoiceId?.quantityLeft);
        setSalesReturnFormData((prev: any) => ({
            ...prev,
            mrp: getDataBasedOnInvoiceId?.mrp,
            invoiceId: value,
            qtyLeft: getDataBasedOnInvoiceId?.quantityLeft,
            invoice: getDataBasedOnInvoiceId?.invoice,
        }));
    }

    // Debounced bill search while the picker step is showing (same idiom the
    // rest of this codebase uses for search-as-you-type).
    useEffect(() => {
        if (!addOpen || selectedBill) return;
        const timer = setTimeout(() => {
            dispatch(getBillingListAction({ searchText: billSearchText, limit: 10, offset: 0 }));
        }, 500);
        return () => clearTimeout(timer);
    }, [addOpen, selectedBill, billSearchText])

    const handleAddOpen = () => {
        setAddOpen(true);
    }

    const handleAddClose = () => {
        setAddOpen(false);
        setBillSearchText("");
        setSelectedBill(null);
        setBillReturnSubmitting(false);
    }

    const handleInvoiceClick = (item: any) => {
        if (!item?.invoice) return;
        dispatch(getBillingListAction({ searchText: item.invoice, limit: 1, offset: 0 }));
        setPendingInvoiceNavigate(item.invoice);
    }

    useEffect(() => {
        if (!pendingInvoiceNavigate || billingListOnload) return;
        const match = (billSearchResults || []).find((row: any) => row?.invoiceId === pendingInvoiceNavigate);
        if (match) {
            navigate("/create/bill", { state: { data: match, isEdit: 1 } });
        }
        setPendingInvoiceNavigate(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingInvoiceNavigate, billingListOnload])

    const handleSelectBill = (bill: any) => {
        setSelectedBill(bill);
        dispatch(getSalesReturnBillDetailsAction({ invoiceId: bill?.billingId }));
    }

    const handleBackToSearch = () => {
        setSelectedBill(null);
    }

    const handleSubmitBillReturn = (item: any, payload: any) => {
        let body = {
            salesReturnId: null,
            productItemId: item?.productId,
            invoiceId: selectedBill?.billingId,
            categoryId: item?.categoryId,
            subCategoryId: item?.subCategoryId,
            qty: payload?.qty,
            returnedAmount: payload?.returnedAmount,
            mrp: Number(item?.mrp),
            isDeleted: 0,
            isMultipleRecords: item?.isMultipleRecords,
            status: payload?.status,
            statusRefId: payload?.statusRefId,
        }
        dispatch(createOrUpdateSalesReturnAction(body));
        setBillReturnSubmitting(true);
    }

    useEffect(() => {
        if (billReturnSubmitting && !createOrUpdateSalesReturnOnLoad) {
            setBillReturnSubmitting(false);
            toast.success("Sales Return added successfully");
            getSalesReturnListApi();
            if (selectedBill?.billingId) {
                dispatch(getSalesReturnBillDetailsAction({ invoiceId: selectedBill.billingId }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billReturnSubmitting, createOrUpdateSalesReturnOnLoad])

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="8" />
            <SalesReturnTable
                data={salesReturnData}
                loading={createOrUpdateSalesReturnOnLoad}
                onMenuAction={handleEditOrDelete}
                onInvoiceClick={handleInvoiceClick}
                searchValue={filterData?.searchText}
                onSearch={getsearchTextdataList}
                searchPlaceholder={"Search by invoice id"}
                title="Sales Return"
                returnTypeFilter={filterData?.returnTypeFilter}
                onReturnTypeFilterChange={handleReturnTypeFilterChange}
                creditFilter={filterData?.creditFilter}
                onCreditFilterChange={handleCreditFilterChange}
                headerActions={<Button onClick={handleAddOpen}>{AppConstant?.add}</Button>}
                pagination={{
                    page: salesReturnPagination?.currentPage || 1,
                    total: salesReturnPagination?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />
            <SalesReturnFormSheet
                open={open}
                onClose={AddProductModalClose}
                formData={salesReturnFormData}
                searchOptions={getSalesReturnSearchData}
                invoiceId={invoiceId}
                categoryList={categoryAndSubCategoryList}
                subCategoryList={subCategoryList}
                quantity={quantity}
                saving={createOrUpdateSalesReturnOnLoad}
                onProductSearch={handleProductIdSearch}
                onProductChange={handleProductIdChange}
                onProductSelect={handleProductIdSelect}
                onInvoiceChange={handleInvoiceId}
                onQtyChange={handleQuantity}
                onChange={(patch: any) => setSalesReturnFormData({ ...salesReturnFormData, ...patch })}
                onStatusChange={(statusRefId: any) => setSalesReturnFormData({ ...salesReturnFormData, statusRefId: statusRefId })}
                onSubmit={createOrUpdateSalesReturnApi}
            />
            <SalesReturnBillFormSheet
                open={addOpen}
                onClose={handleAddClose}
                billSearchText={billSearchText}
                onBillSearchChange={setBillSearchText}
                billSearchResults={billSearchResults}
                billSearchLoading={billingListOnload}
                selectedBill={selectedBill}
                onSelectBill={handleSelectBill}
                onBackToSearch={handleBackToSearch}
                billDetails={billReturnDetails}
                billDetailsLoading={getSalesReturnBillDetailsOnLoad}
                submitting={billReturnSubmitting}
                onSubmitReturn={handleSubmitBillReturn}
            />
            <ConfirmDialog
                open={openDeleteModal}
                onOpenChange={(o: boolean) => { if (!o) handleDeleteModalClose(); }}
                title="Delete Sales Record"
                description="Do you want to Delete this record ..?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
                destructive
                onConfirm={createOrUpdateSalesReturnApi}
            />
        </div>
    );
}

export default SalesReturn;
